import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
} from "@mui/material";

/**
 * ContentSection - A reusable component for content sections with consistent styling
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display in the section
 * @param {string} props.title - Section title
 * @param {React.ReactNode} props.icon - Optional icon to display next to the title
 * @param {React.ReactNode} props.actions - Optional action buttons/components to display in the header
 * @param {boolean} props.divider - Whether to show a divider below the title
 * @param {boolean} props.elevation - Card elevation (0-24)
 * @param {Object} props.sx - Additional MUI sx styles for the Card component
 * @param {Object} props.headerSx - Additional MUI sx styles for the CardHeader
 * @param {Object} props.contentSx - Additional MUI sx styles for the CardContent
 * @returns {React.ReactElement}
 */
const ContentSection = ({
  children,
  title,
  icon = null,
  actions = null,
  divider = false,
  elevation = 1,
  sx = {},
  headerSx = {},
  contentSx = {},
}) => {
  return (
    <Card
      elevation={elevation}
      sx={{
        mb: 4,
        borderRadius: 2,
        overflow: "hidden",
        ...sx,
      }}
    >
      {title && (
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {icon && (
                <Box
                  sx={{
                    mr: 1,
                    display: "flex",
                    alignItems: "center",
                    color: "primary.main",
                  }}
                >
                  {icon}
                </Box>
              )}
              <Typography variant="h6" component="h2" fontWeight="medium">
                {title}
              </Typography>
            </Box>
          }
          action={actions}
          sx={{
            p: 2.5,
            ...headerSx,
          }}
        />
      )}

      {divider && <Divider />}

      <CardContent
        sx={{
          p: title ? 2.5 : 2.5,
          "&:last-child": { pb: 2.5 },
          ...contentSx,
        }}
      >
        {children}
      </CardContent>
    </Card>
  );
};

ContentSection.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  icon: PropTypes.node,
  actions: PropTypes.node,
  divider: PropTypes.bool,
  elevation: PropTypes.number,
  sx: PropTypes.object,
  headerSx: PropTypes.object,
  contentSx: PropTypes.object,
};

export default ContentSection;
